import { useEffect, useState } from "react";
import { ButtonCustom } from "../../../../components/custom/button-custom";
import { ModalCustom } from "../../../../components/custom/modal-custom";
import { AttendanceService } from "../services/attendance-service";
import { convertDateFormat } from "../../../../utils/time-utils";
import { LoadingOverlay } from "@mantine/core";
import { useDataResponse } from "../../../../hooks/useResponseHandler";
import { AttendanceRecord } from "../types/attendance";
import ImageCustom from "../../../../components/custom/image-custom";
import { LabelStatusCustom } from "../../../../components/custom/label-status-custom";
import { ABSENCE_TYPE, ATTENDANCE_ISSUE_TYPE, ATTENDANCE_STATUS, CHECK_IN_LOCATION_TYPE } from "../../../../constants/status";
import Tippy from "@tippyjs/react";
import { IconEye } from "../../../../components/icons/icon-group";
import IconCalendarCheck from "../../../../components/icons/icon-calendar-check";
import IconArrow from "../../../../components/icons/icon-arrow";
import IconRepeat from "../../../../components/icons/icon-repeat";
import { parse, isBefore, format, startOfDay } from "date-fns";
import { vi } from "date-fns/locale";

const AttendanceManagementMonthlyModal = ({
    isShow,
    onClose,
    selectedItem,
    refreshTable,
    setRefreshTable,
    onShowModalConfirm,
    onShowDetail
}: {
    isShow: boolean;
    onClose: () => void;
    selectedItem: AttendanceRecord;
    refreshTable: boolean
    setRefreshTable: (value: boolean) => void
    onShowModalConfirm: ({
        employeeId, workDate, issueType
    }: {
        employeeId: number
        workDate: string
        issueType: number
    }) => void
    onShowDetail: (itemSelected: AttendanceRecord[]) => void
}) => {

    const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
    const [dateState, setDateState] = useState({ day: "", month: "", year: "" });
    const [heightTable, setHeightTable] = useState(0);
    const [screenSize, setScreenSize] = useState('desktop');

    const { fetch: onGetAttendance, response: resAttendance, loading } = AttendanceService.getAttendance();

    useEffect(() => {
        if (!isShow) { return }
        onGetAttendance({
            month: convertDateFormat(selectedItem.date, 'dd/MM/yyyy', 'MM/yyyy'),
            user_id: selectedItem.employee.id,
        })
        const [day, month, year] = selectedItem.date.split("/")
        setDateState({ day, month, year })
    }, [isShow])

    useEffect(() => {
        if (!refreshTable) { return }
        onGetAttendance({
            month: convertDateFormat(selectedItem.date, 'dd/MM/yyyy', 'MM/yyyy'),
            user_id: selectedItem.employee.id,
        })
        const [day, month, year] = selectedItem.date.split("/")
        setDateState({ day, month, year })
        setRefreshTable(false)
    }, [refreshTable])

    useEffect(() => {
        if (!isShow) { return }
        const handleResize = () => {
            const filter = document.getElementById("attendance-record-statistics") as HTMLElement;
            const legend = document.getElementById("legend-attendance-table") as HTMLElement;
            if (filter && legend) {
                const filterHeight = filter.offsetHeight;
                const legendHeight = legend.offsetHeight;
                const viewportHeight = window.innerHeight;
                const viewportWidth = window.innerWidth;
                if (viewportWidth <= 640) setScreenSize('mobile');
                else if (viewportWidth <= 1024) setScreenSize('tablet');
                else if (viewportHeight <= 800) setScreenSize('laptop');
                else setScreenSize('desktop');
                // Tính toán chiều cao còn lại
                const baseOffset = 292;
                let calculatedHeight;
                // Responsive breakpoints
                switch (screenSize) {
                    case 'mobile':
                        calculatedHeight = Math.min(300, window.innerHeight - filterHeight - legendHeight - baseOffset);
                        break;
                    case 'tablet':
                        calculatedHeight = Math.min(400, window.innerHeight - filterHeight - legendHeight - baseOffset);
                        break;
                    case 'laptop':
                        calculatedHeight = Math.min(450, window.innerHeight - filterHeight - legendHeight - baseOffset);
                        break;
                    default:
                        calculatedHeight = window.innerHeight - filterHeight - legendHeight - baseOffset;
                }
                // Đảm bảo chiều cao tối thiểu
                setHeightTable(Math.max(250, calculatedHeight));
            }
        };
        requestAnimationFrame(handleResize); // Đảm bảo tính toán sau khi DOM đã cập nhật
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [screenSize, isShow]);

    useDataResponse(resAttendance, setAttendanceData)

    // Utility functions
    const getDaysInMonth = (month: number, year: number): number => {
        return new Date(year, month, 0).getDate();
    };

    const getDayOfWeek = (day: number, month: number, year: number): number => {
        return new Date(year, month - 1, day).getDay();
    };

    const formatTime = (datetime: string): string => {
        if (!datetime) return '';
        return convertDateFormat(datetime, 'dd/MM/yyyy HH:mm', 'HH:mm')
    };

    const getAttendanceByDate = (day: number): AttendanceRecord[] => {
        const dateStr = `${day.toString().padStart(2, '0')}/${dateState.month.padStart(2, '0')}/${dateState.year}`;
        return attendanceData.filter(record => record.date === dateStr);
    };

    const isToday = (day: number): boolean => {
        const today = new Date();
        return today.getDate() === day &&
            today.getMonth() === Number(dateState.month) - 1 &&
            today.getFullYear() === Number(dateState.year);
    };

    const getDayName = (day: number): string => {
        const dayOfWeek = getDayOfWeek(day, Number(dateState.month), Number(dateState.year));
        const days = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
        return days[dayOfWeek];
    };

    const attendanceStatusMap: Record<number, { content: string; backgroundColor: string; textColor: string }> = {
        [ATTENDANCE_STATUS.NONE]: { content: "Không có", backgroundColor: "bg-gray-100", textColor: "text-gray-700" },
        [ATTENDANCE_STATUS.ON_TIME]: { content: "Đúng giờ", backgroundColor: "bg-green-100", textColor: "text-green-700" },
        [ATTENDANCE_STATUS.LATE]: { content: "Muộn", backgroundColor: "bg-yellow-100", textColor: "text-yellow-700" },
        [ATTENDANCE_STATUS.LEAVE_EARLY]: { content: "Về sớm", backgroundColor: "bg-yellow-100", textColor: "text-yellow-700" },
        [ATTENDANCE_STATUS.NOT_CHECKED_IN]: { content: "Chưa check in", backgroundColor: "bg-red-100", textColor: "text-red-700" },
        [ATTENDANCE_STATUS.NOT_CHECKED_OUT]: { content: "Chưa check out", backgroundColor: "bg-red-100", textColor: "text-red-700" },
    };

    const checkInLocationMap: Record<number, { content: string; backgroundColor: string; textColor: string }> = {
        [CHECK_IN_LOCATION_TYPE.OUTSIDE_COMPANY]: { content: "Ngoài công ty", backgroundColor: "bg-orange-100", textColor: "text-orange-700" },
        [CHECK_IN_LOCATION_TYPE.INSIDE_COMPANY]: { content: "Trong công ty", backgroundColor: "bg-blue-100", textColor: "text-blue-700" },
    };

    const getStatusBadge = (attendance: AttendanceRecord | null, isCheckIn: boolean) => {
        if (!attendance) return null;
        let badge
        if (isCheckIn) {
            badge = attendanceStatusMap[attendance.check_in_status] ?? attendanceStatusMap[ATTENDANCE_STATUS.NONE];
        } else {
            badge = attendanceStatusMap[attendance.check_out_status] ?? attendanceStatusMap[ATTENDANCE_STATUS.NONE];
        }
        return <LabelStatusCustom
            content={badge.content}
            textColor={badge.textColor}
            backgroundColor={badge.backgroundColor}
            className="min-w-28"
        />
    };

    const getCheckInLocationBadge = (attendance: AttendanceRecord | null, isCheckIn: boolean) => {
        if (!attendance) return null;
        let badge
        if (isCheckIn) {
            badge = checkInLocationMap[attendance.check_in_scope];
        } else {
            badge = checkInLocationMap[attendance.check_out_scope];
        }
        return <LabelStatusCustom
            content={badge?.content ?? "Không xác định"}
            backgroundColor={badge?.backgroundColor ?? "bg-gray-100"}
            textColor={badge?.textColor ?? "text-gray-700"}
            className="min-w-28"
        />
    };

    const daysInMonth = getDaysInMonth(Number(dateState.month), Number(dateState.year));
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    // Statistics
    const attendedDays = days.filter(day => {
        const records = getAttendanceByDate(day);
        return records.some(r => r.check_in_at); // ít nhất 1 lần check in
    }).length;
    const absentDays = days.filter(day => {
        const records = getAttendanceByDate(day);
        return records.some(r => !r.check_in_at); // ít nhất 1 lần không check in
    }).length;
    const lateDays = days.filter(day => {
        const records = getAttendanceByDate(day);
        return records.some(r => r.minutes_late > 0); // có ít nhất 1 lần đi muộn
    }).length;
    const earlyDays = days.filter(day => {
        const records = getAttendanceByDate(day);
        return records.some(r => r.minutes_early > 0); // có ít nhất 1 lần về sớm
    }).length;

    const getRowHighlight = (records: AttendanceRecord[]): string => {
        if (records.some(r => r.check_out_status === ATTENDANCE_STATUS.NOT_CHECKED_OUT)) {
            return "bg-red-50 border-red-200 hover:bg-red-50"; // Chưa check out
        }
        if (records.length > 1) {
            return "bg-emerald-50 border-emerald-200 hover:bg-emerald-50"; // Chấm công nhiều lần
        }
        if (records.some(r => r.minutes_late > 0 && r.minutes_early > 0)) {
            return "bg-orange-50 border-orange-200 hover:bg-orange-50"; // Đi muộn + Về sớm
        }
        if (records.some(r => r.minutes_late > 0)) {
            return "bg-yellow-50 border-yellow-200 hover:bg-yellow-50"; // Đi muộn
        }
        if (records.some(r => r.minutes_early > 0)) {
            return "bg-purple-50 border-purple-200 hover:bg-purple-50"; // Về sớm
        }
        return "";
    };

    const renderCheckLabel = (dateTime: string, timeString: string, label: string) => {
        const now = new Date();

        const date = dateTime
            ? parse(dateTime, "dd/MM/yyyy", new Date(), { locale: vi })
            : null;

        const time = timeString
            ? parse(timeString, "dd/MM/yyyy HH:mm", new Date(), { locale: vi })
            : null;

        let content = "";
        let backgroundColor = "";
        let textColor = "";

        if (time && !isNaN(time.getTime())) {
            // Có giờ check in/out
            content = `${format(time, "HH:mm")}`;
            backgroundColor = "bg-green-100";
            textColor = "text-green-700";
        } else if (date && isBefore(startOfDay(date), startOfDay(now))) {
            // Ngày đã qua nhưng không có giờ check
            content = `Quên ${label.toLowerCase()}`;
            backgroundColor = "bg-red-100";
            textColor = "text-red-700";
        } else {
            // Ngày hôm nay hoặc tương lai, chưa check
            content = `Chưa ${label.toLowerCase()}`;
            backgroundColor = "bg-orange-100";
            textColor = "text-orange-700";
        }

        return (
            <LabelStatusCustom
                content={content}
                backgroundColor={backgroundColor}
                textColor={textColor}
                className="min-w-32"
            />
        );
    };

    const footerModal = (
        <ButtonCustom
            type="button"
            text="Đóng"
            onClick={onClose}
            className="text-gray-500 uppercase bg-gray-100 btn hover:bg-gray-400 hover:text-white"
        />
    )

    const bodyModal = (
        <div className="relative overflow-hidden panel">
            <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 1 }} />
            {/* Statistics */}
            <div className="p-6 border-b bg-gray-50 rounded-t-md" id="attendance-record-statistics">
                <div className="flex flex-wrap gap-4 lg:flex-nowrap">
                    <div className="flex items-center justify-center gap-4 p-4 text-gray-600 bg-white rounded-lg shadow-md lg:min-w-80 lg:justify-normal">
                        <div className="w-16 h-16 overflow-hidden rounded-full">
                            <ImageCustom url={selectedItem.employee.avatar} className="w-full h-full" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-semibold">{selectedItem.employee.name || 'Nhân viên'}</h1>
                            <p className="opacity-90">{selectedItem.employee.department_name || 'Phòng ban'}</p>
                        </div>
                    </div>
                    <div className="grid flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
                        <div className="flex flex-col items-center justify-center p-4 text-center bg-white rounded-lg shadow-md">
                            <div className="text-2xl font-bold text-green-600">{attendedDays}</div>
                            <div className="text-sm text-gray-600">Ngày có mặt</div>
                        </div>
                        <div className="flex flex-col items-center justify-center p-4 text-center bg-white rounded-lg shadow-md">
                            <div className="text-2xl font-bold text-red-600">{absentDays}</div>
                            <div className="text-sm text-gray-600">Ngày vắng mặt</div>
                        </div>
                        <div className="flex flex-col items-center justify-center p-4 text-center bg-white rounded-lg shadow-md">
                            <div className="text-2xl font-bold text-yellow-600">{lateDays}</div>
                            <div className="text-sm text-gray-600">Ngày đi muộn</div>
                        </div>
                        <div className="flex flex-col items-center justify-center p-4 text-center bg-white rounded-lg shadow-md">
                            <div className="text-2xl font-bold text-purple-600">{earlyDays}</div>
                            <div className="text-sm text-gray-600">Ngày về sớm</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Legend */}
            <div className="p-4 border-t border-b border-gray-200 bg-gray-50" id="legend-attendance-table">
                <h3 className="mb-2 font-semibold text-gray-700">Chú thích</h3>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-red-200 rounded-full bg-red-50"></div>
                        <span>Chưa check out</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-orange-200 rounded-full bg-orange-50"></div>
                        <span>Đi muộn + Về sớm</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-yellow-300 rounded-full bg-yellow-50"></div>
                        <span>Đi muộn</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-purple-200 rounded-full bg-purple-50"></div>
                        <span>Về sớm</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-200 rounded-full bg-blue-50"></div>
                        <span>Hôm nay</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 rounded-full bg-emerald-50 border-emerald-200"></div>
                        <span>Chấm công nhiều lần</span>
                    </div>

                </div>
            </div>

            {/* Attendance Table */}
            <div
                className="overflow-auto max-w-[calc(90vw-4.5rem)]"
                style={{ height: heightTable }}
            >
                <table className="w-full border-collapse">
                    <thead className="sticky top-0 z-10 bg-gray-50">
                        <tr>
                            <th className="p-4 font-semibold text-left text-gray-700 border-b-2 border-gray-200 min-w-40">Ngày chấm công</th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-52">
                                <div className="flex flex-col">
                                    <span>Thời gian</span>
                                    <span className="text-xs font-normal">(Check in - Check out)</span>
                                </div>
                            </th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-56">Ca làm việc</th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-48"><span>Trạng thái<br />check in</span></th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-48"><span>Trạng thái<br />check out</span></th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-28">Đi muộn</th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-28">Về sớm</th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-40"><span>Vị trí<br />check in</span></th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-40"><span>Vị trí<br />check out</span></th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200"><span>Ghi chú<br />duyệt phiếu</span></th>
                            <th className="p-4 font-semibold text-center text-gray-700 border-b-2 border-gray-200 min-w-32">Hành động</th>
                        </tr>
                    </thead>
                    <tbody>
                        {days.map(day => {
                            const records = getAttendanceByDate(day);
                            const today = isToday(day);
                            const highlightClass = getRowHighlight(records);

                            return (
                                <tr
                                    key={day}
                                    className={`border-b transition-colors 
                                    ${today
                                            ? "bg-blue-50 border-blue-200 hover:bg-blue-50"
                                            : "border-gray-100 hover:bg-gray-50"
                                        } ${highlightClass}`}
                                >
                                    {/* Ngày chấm công */}
                                    <td className="p-4">
                                        <div className="flex flex-col">
                                            <span className={`font-semibold ${today ? 'text-blue-700' : 'text-gray-900'}`}>
                                                {String(day).padStart(2, "0")}/{dateState.month}/{dateState.year}
                                            </span>
                                            <span className={`text-sm ${today ? 'text-blue-600' : 'text-gray-500'}`}>
                                                {getDayName(day)}
                                            </span>
                                            {today && (
                                                <LabelStatusCustom
                                                    content='Hôm nay'
                                                    textColor="text-blue-700"
                                                    backgroundColor="bg-blue-100"
                                                    className="w-fit"
                                                />
                                            )}
                                        </div>
                                    </td>
                                    {/* Thời gian: Check in - Check out */}
                                    <td className="p-4 text-center">
                                        {records.length > 0 ? (
                                            <div className="flex flex-col gap-1">
                                                {records.map(r => (
                                                    <div key={r.id} className="flex items-center justify-center gap-1">
                                                        {renderCheckLabel(r.date, r.check_in_at, 'Check in')}
                                                        <span className="text-gray-500">-</span>
                                                        {renderCheckLabel(r.date, r.check_out_at, 'Check out')}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>

                                    {/* Ca làm việc */}
                                    <td className="p-4 text-center">
                                        {records.length > 0 ? (
                                            <>
                                                {records.some(r => r.shift && r.absence_type !== ABSENCE_TYPE.NO_SHIFT)
                                                    ? (
                                                        <div key={records[records.length - 1].id} className='flex items-stretch justify-center w-full p-2 text-left text-blue-500 border border-blue-500 rounded-md bg-blue-500/10'>
                                                            <span className="flex-shrink-0 w-1 bg-blue-500 rounded-full" />
                                                            <div className="flex flex-col flex-1 px-2 py-1 break-words whitespace-normal">
                                                                <span className="text-base font-bold">{records[records.length - 1].shift.start_time} - {records[records.length - 1].shift.end_time}</span>
                                                                <span className="text-xs">{records[records.length - 1].shift.name}</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )
                                                }
                                            </>
                                        ) : <span className="text-gray-400">-</span>}
                                    </td>

                                    {/* Trạng thái check in */}
                                    <td className="p-4 text-center">
                                        <div className="flex flex-col gap-1">
                                            {records.map(r => (
                                                <div key={r.id}>{getStatusBadge(r, true)}</div>
                                            ))}
                                        </div>
                                    </td>
                                    {/* Trạng thái check out */}
                                    <td className="p-4 text-center">
                                        <div className="flex flex-col gap-1">
                                            {records.map(r => (
                                                <div key={r.id}>{getStatusBadge(r, false)}</div>
                                            ))}
                                        </div>
                                    </td>
                                    {/* Đi muộn */}
                                    <td className="p-4 text-center">
                                        <div className="flex flex-col gap-1">
                                            {records.map(r => (
                                                r.minutes_late > 0 ? (
                                                    <LabelStatusCustom
                                                        key={r.id}
                                                        content={`${r.minutes_late} phút`}
                                                        textColor="text-orange-700"
                                                        backgroundColor="bg-orange-100"
                                                        className="min-w-20"
                                                    />
                                                ) : (
                                                    <span key={r.id} className="text-gray-400">-</span>
                                                )
                                            ))}
                                        </div>
                                    </td>
                                    {/* Về sớm */}
                                    <td className="p-4 text-center">
                                        <div className="flex flex-col gap-1">
                                            {records.map(r => (
                                                r.minutes_early > 0 ? (
                                                    <LabelStatusCustom
                                                        key={r.id}
                                                        content={`${r.minutes_early} phút`}
                                                        textColor="text-purple-700"
                                                        backgroundColor="bg-purple-100"
                                                        className="min-w-20"
                                                    />
                                                ) : (
                                                    <span key={r.id} className="text-gray-400">-</span>
                                                )
                                            ))}
                                        </div>
                                    </td>
                                    {/* Vị trí check in */}
                                    <td className="p-4 text-center">
                                        <div className="flex flex-col gap-1">
                                            {records.map(r => (
                                                <div key={r.id}>{getCheckInLocationBadge(r, true)}</div>
                                            ))}
                                        </div>
                                    </td>
                                    {/* Vị trí check out */}
                                    <td className="p-4 text-center">
                                        <div className="flex flex-col gap-1">
                                            {records.map(r => (
                                                <div key={r.id}>{getCheckInLocationBadge(r, false)}</div>
                                            ))}
                                        </div>
                                    </td>
                                    {/* Ghi chú duyệt phiếu */}
                                    <td className="p-4 text-center">
                                        <div className="m-auto min-w-40 max-w-96">
                                            {records.length > 0 ? (
                                                <span className="break-words whitespace-normal">{records[records.length - 1]?.note || "-"}</span>
                                            ) : (<span className="text-gray-400">-</span>)}
                                        </div>
                                    </td>
                                    {/* Hành động */}
                                    <td className="p-4 text-center">
                                        <div className="flex justify-center gap-2">
                                            {records.length > 0 ? (
                                                <>
                                                    {records.some(item => item.check_in_status === ATTENDANCE_STATUS.LATE) && (
                                                        <Tippy content="Duyệt check-in muộn">
                                                            <button type="button" onClick={() => onShowModalConfirm({
                                                                employeeId: records[0].employee_id,
                                                                workDate: records[0].date,
                                                                issueType: ATTENDANCE_ISSUE_TYPE.LATE_CHECK_IN
                                                            })}>
                                                                <IconCalendarCheck className="fill-orange-500" />
                                                            </button>
                                                        </Tippy>
                                                    )}
                                                    {records.some(item => item.check_out_status === ATTENDANCE_STATUS.LEAVE_EARLY) && (
                                                        <Tippy content="Duyệt check-out sớm">
                                                            <button type="button" onClick={() => onShowModalConfirm({
                                                                employeeId: records[0].employee_id,
                                                                workDate: records[0].date,
                                                                issueType: ATTENDANCE_ISSUE_TYPE.EARLY_CHECK_OUT
                                                            })}>
                                                                <IconArrow direction="right" className="fill-red-500" />
                                                            </button>
                                                        </Tippy>
                                                    )}
                                                    {records.length > 1 && (
                                                        <Tippy content="Duyệt chấm công nhiều lần">
                                                            <button type="button" onClick={() => onShowModalConfirm({
                                                                employeeId: records[0].employee_id,
                                                                workDate: records[0].date,
                                                                issueType: ATTENDANCE_ISSUE_TYPE.MULTIPLE_CHECK_INS
                                                            })}>
                                                                <IconRepeat className="w-5 h-5 fill-green-500" />
                                                            </button>
                                                        </Tippy>
                                                    )}
                                                    <Tippy content="Chi tiết">
                                                        <button type="button" onClick={() => onShowDetail(records)}>
                                                            <IconEye />
                                                        </button>
                                                    </Tippy>
                                                </>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )

    return (
        <ModalCustom
            isOpen={isShow}
            onClose={onClose}
            titleModal={`Chi tiết chấm công tháng ${dateState.month}/${dateState.year}`}
            modalSize="100%"
            footerModal={footerModal}
            bodyModal={bodyModal}
        />
    );
}

export default AttendanceManagementMonthlyModal;